import { CloudUploadIcon } from "lucide-react";
import DatePicker from "react-datepicker";
import { de } from 'date-fns/locale';
import type { ChangeEvent, ReactNode } from "react";
import { Controller, type FieldValues, type RegisterOptions, type UseFormReturn } from "react-hook-form";
import {
    TextField,
    Checkbox,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    Box,
    Slider,
    Chip,
    Button,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from "./Typography";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";

interface OptionItem {
    name: string;
    value: string | number | boolean;
}

interface ChipItem {
    label: string;
    onDelete?: () => void;
    onClick?: () => void;
}

interface InputProps<TFieldValues extends FieldValues = FieldValues> {
    type?: string;
    label?: string | ReactNode;
    placeholder?: string;
    name?: string;
    id?: string;
    formRules?: UseFormReturn<TFieldValues>;
    rows?: number;
    checkboxDescription?: string;
    checked?: boolean;
    onClick?: () => void;
    options?: OptionItem[];
    validation?: RegisterOptions<TFieldValues>;
    step?: string | number;
    min?: string | number;
    max?: string | number;
    autoComplete?: string;
    noLabel?: boolean;
    value?: string | number | boolean | Array<string | number>;
    onChange?: (value: unknown) => void;
    sx?: SxProps<Theme>;
    description?: string;
    defaultValue?: number;
    chipProps?: ChipItem[];
    hideSelectedChips?: boolean;
    selectAllLabel?: string;
    disabled?: boolean;
}

const Input = <TFieldValues extends FieldValues = FieldValues>({
    type,
    label,
    placeholder,
    name,
    id,
    formRules,
    rows = 3,
    checkboxDescription,
    checked,
    onClick,
    options = [],
    validation = {},
    step = "",
    min = "",
    max = "",
    autoComplete = "",
    value,
    onChange,
    sx,
    description,
    defaultValue,
    chipProps,
    hideSelectedChips = false,
    selectAllLabel,
    disabled = false
}: InputProps<TFieldValues>) => {
    const { t } = useTranslation();
    const toSelectValue = (optionValue: OptionItem["value"]): string | number =>
        typeof optionValue === "boolean" ? String(optionValue) : optionValue;
    const getFieldErrorMessage = (fieldName?: string) => {
        if (!fieldName || !formRules) return undefined;
        const raw = formRules.formState?.errors?.[fieldName as keyof typeof formRules.formState.errors];
        if (!raw || typeof raw !== "object") return undefined;
        const maybeMessage = (raw as { message?: unknown }).message;
        return typeof maybeMessage === "string" ? maybeMessage : undefined;
    };

    if (type == 'text' || type == 'email' || type == 'password') {
        const fieldErrorMessage = getFieldErrorMessage(name);
        return (
            <Box sx={sx}>
                <Typography variant="subtitle">{label}</Typography>
                <Typography variant="label">{description}</Typography>
                <TextField
                    id={id}
                    type={type}
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                    fullWidth
                    variant="outlined"
                    required={Boolean(validation?.required)}
                    error={!!fieldErrorMessage}
                    helperText={fieldErrorMessage}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-error': {
                                '& fieldset': {
                                    borderColor: '#ef4444 !important',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#dc2626 !important',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#dc2626 !important',
                                },
                            },
                        },
                        '& textarea': {
                            resize: 'vertical',
                        },
                        '& .MuiInputBase-inputMultiline': {
                            resize: 'vertical',
                        },
                        '& .MuiFormHelperText-root.Mui-error': {
                            color: '#ef4444',
                            fontWeight: 500,
                        },
                    }}
                    disabled={disabled}
                    {...(formRules && name ? formRules.register(name as never, validation) : {})}
                />
            </Box>
        )
    }

    if (type == 'number') {
        const fieldErrorMessage = getFieldErrorMessage(name);
        return (
            <Box sx={sx}>
                <Typography variant="subtitle">{label}</Typography>
                <Typography variant="label">{description}</Typography>
                <TextField
                    id={id}
                    type="number"
                    placeholder={placeholder}
                    fullWidth
                    variant="outlined"
                    required={Boolean(validation?.required)}
                    error={!!fieldErrorMessage}
                    helperText={fieldErrorMessage}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-error': {
                                '& fieldset': {
                                    borderColor: '#ef4444 !important',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#dc2626 !important',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#dc2626 !important',
                                },
                            },
                        },
                        '& .MuiFormHelperText-root.Mui-error': {
                            color: '#ef4444',
                            fontWeight: 500,
                        },
                    }}
                    disabled={disabled}
                    {...(formRules && name ? formRules.register(name as never, validation) : {})}
                    slotProps={{
                        htmlInput: {
                            step: step,
                            min: min,
                            max: max,
                        }
                    }}
                />
            </Box>
        )
    }

    if (type == 'textarea') {
        const fieldErrorMessage = getFieldErrorMessage(name);
        return (
            <Box sx={sx}>
                <Typography variant="subtitle">{label}</Typography>
                <Typography variant="label">{description}</Typography>
                <TextField
                    id={id}
                    placeholder={placeholder}
                    multiline
                    rows={rows}
                    fullWidth
                    variant="outlined"
                    error={!!fieldErrorMessage}
                    helperText={fieldErrorMessage}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-error': {
                                '& fieldset': {
                                    borderColor: '#ef4444 !important',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#dc2626 !important',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#dc2626 !important',
                                },
                            },
                        },
                        '& .MuiFormHelperText-root.Mui-error': {
                            color: '#ef4444',
                            fontWeight: 500,
                        },
                    }}
                    disabled={disabled}
                    {...(formRules && name ? formRules.register(name as never, validation) : {})}
                />
            </Box>
        )
    }

    if (type == 'checkbox') {
        if (checkboxDescription) {
            const fieldErrorMessage = getFieldErrorMessage(name);
            return (
                <Box 
                    sx={{
                        border: 1,
                        borderColor: checked ? 'primary.main' : 'grey.300',
                        bgcolor: checked ? 'primary.50' : 'transparent',
                        borderRadius: 1,
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': {
                            bgcolor: 'grey.50'
                        },
                        opacity: disabled ? 0.6 : 1,
                        pointerEvents: disabled ? 'none' : 'auto'
                    }}
                    onClick={disabled ? undefined : onClick}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={checked}
                                onChange={() => {}}
                                color="primary"
                                disabled={disabled}
                            />
                        }
                        label={
                            <Box>
                                <Box component="span" sx={{ fontWeight: 'medium' }}>
                                    {label}
                                </Box>
                                <Box component="p" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                                    {checkboxDescription}
                                </Box>
                            </Box>
                        }
                    />
                    {fieldErrorMessage && (
                        <FormHelperText error>{fieldErrorMessage}</FormHelperText>
                    )}
                </Box>
            )
        } else {
            const fieldErrorMessage = getFieldErrorMessage(name);
            return (
                <Box sx={sx}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id={id}
                                color="primary"
                                checked={checked}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    onChange?.(e.target.checked)
                                    if (formRules && name) {
                                        formRules.setValue(name as never, e.target.checked as never)
                                    }
                                }}
                                disabled={disabled}
                                {...(formRules && name ? { name: formRules.register(name as never, validation).name, onBlur: formRules.register(name as never, validation).onBlur, ref: formRules.register(name as never, validation).ref } : {})}
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {label}
                            </Box>
                        }
                    />
                    {fieldErrorMessage && (
                        <FormHelperText error>{fieldErrorMessage}</FormHelperText>
                    )}
                </Box>
            )
        }
    }

    if (type == 'dateRange') {
        return (
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: 1 }}>
                        <TextField
                            label="Startdatum"
                            fullWidth
                            required
                            onClick={() => {}}
                            value={(() => {
                                const start = formRules?.watch('startDate' as never);
                                return start instanceof Date ? start.toLocaleDateString('de-DE') : '';
                            })()}
                            disabled={disabled}
                        />
                        <DatePicker
                            selected={(formRules?.watch('startDate' as never) instanceof Date ? formRules.watch('startDate' as never) : null) as Date | null}
                            onChange={(date: Date | null) => date && formRules?.setValue('startDate' as never, date as never)}
                            dateFormat="dd.MM.yyyy"
                            locale={de}
                            disabled={disabled}
                            customInput={<div style={{ display: 'none' }} />}
                        />
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="Enddatum"
                            fullWidth
                            required
                            error={Boolean(getFieldErrorMessage('endDate'))}
                            helperText={getFieldErrorMessage('endDate')}
                            onClick={() => {}}
                            value={(() => {
                                const end = formRules?.watch('endDate' as never);
                                return end instanceof Date ? end.toLocaleDateString('de-DE') : '';
                            })()}
                            disabled={disabled}
                        />
                        <DatePicker
                            selected={(formRules?.watch('endDate' as never) instanceof Date ? formRules.watch('endDate' as never) : null) as Date | null}
                            onChange={(date: Date | null) => date && formRules?.setValue('endDate' as never, date as never)}
                            dateFormat="dd.MM.yyyy"
                            locale={de}
                            minDate={(formRules?.watch('startDate' as never) instanceof Date ? formRules.watch('startDate' as never) : undefined) as Date | undefined}
                            disabled={disabled}
                            customInput={<div style={{ display: 'none' }} />}
                        />
                    </Box>
                </Box>
            )
    }

    if (type == 'select') {
        const fieldErrorMessage = getFieldErrorMessage(name);
        return (
            <Box sx={sx}>
                <FormControl fullWidth variant="outlined" error={!!fieldErrorMessage}>
                    <Typography variant="subtitle">{label}</Typography>
                    <Typography variant="label">{description}</Typography>
                    {formRules && name ? (
                        <Controller
                            name={name as never}
                            control={formRules.control}
                            rules={validation}
                            defaultValue={"" as never}
                            render={({ field }) => (
                                <Select
                                    labelId={`${id}-label`}
                                    id={id}
                                    {...field}
                                    value={typeof field.value === "boolean" ? String(field.value) : (field.value ?? "")}
                                    MenuProps={{
                                        disableAutoFocusItem: true,
                                        PaperProps: {
                                            sx: {
                                                maxWidth: 360,
                                                width: 360,
                                                maxHeight: 320,
                                                overflowY: 'auto',
                                            },
                                        },
                                        MenuListProps: {
                                            dense: true,
                                            sx: {
                                                py: 0.5,
                                            },
                                        },
                                    }}
                                    error={!!fieldErrorMessage}
                                    sx={{
                                        '&.Mui-error': {
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#ef4444 !important',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#dc2626 !important',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#dc2626 !important',
                                            },
                                        },
                                    }}
                                    disabled={disabled}
                                >
                                    {options.map((option, index) => (
                                        <MenuItem key={index} value={toSelectValue(option.value)}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    ) : (
                        <Select
                            labelId={`${id}-label`}
                            id={id}
                            label={label}
                            value={typeof value === "boolean" ? String(value) : (value ?? '')}
                            onChange={(event) => onChange?.(event.target.value)}
                            MenuProps={{
                                disableAutoFocusItem: true,
                                PaperProps: {
                                    sx: {
                                        maxWidth: 360,
                                        width: 360,
                                        maxHeight: 320,
                                        overflowY: 'auto',
                                    },
                                },
                                MenuListProps: {
                                    dense: true,
                                    sx: {
                                        py: 0.5,
                                    },
                                },
                            }}
                            disabled={disabled}
                        >
                            {options.map((option, index) => (
                                <MenuItem key={index} value={toSelectValue(option.value)}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    {fieldErrorMessage && (
                        <FormHelperText sx={{ color: '#ef4444', fontWeight: 500 }}>{fieldErrorMessage}</FormHelperText>
                    )}
                </FormControl>
            </Box>
        )
    }

    if (type == 'date') {
        const fieldErrorMessage = getFieldErrorMessage(name);
        return (
            <Box sx={sx}>
                <TextField
                    id={id}
                    type="date"
                    label={label}
                    fullWidth
                    variant="outlined"
                    required={Boolean(validation?.required)}
                    error={!!fieldErrorMessage}
                    helperText={fieldErrorMessage}
                    disabled={disabled}
                    {...(formRules && name ? formRules.register(name as never, validation) : {})}
                />
            </Box>
        )
    }

    if (type == 'slider') {
        const fieldErrorMessage = getFieldErrorMessage(name);
        const minValue = typeof min === 'string' ? parseFloat(min) : (min || 0);
        const maxValue = typeof max === 'string' ? parseFloat(max) : (max || 100);
        const stepValue = typeof step === 'string' ? parseFloat(step) : (step || 1);
        
        return (
            <Box sx={sx}>
                <Typography variant="subtitle">{label}</Typography>
                <Typography variant="label">{description}</Typography>
                {formRules && name ? (
                    <Controller
                        name={name as never}
                        control={formRules.control}
                        rules={validation}
                        defaultValue={(defaultValue || minValue) as never}
                        render={({ field }) => (
                            <Slider
                                id={id}
                                value={typeof field.value === 'number' ? field.value : parseFloat(String(field.value ?? minValue)) || minValue}
                                onChange={(_, newValue) => {
                                    // Ensure we always pass a number, not a string
                                    const numValue = Array.isArray(newValue) ? newValue[0] : newValue;
                                    field.onChange(numValue);
                                }}
                                min={minValue}
                                max={maxValue}
                                step={stepValue}
                                valueLabelDisplay="auto"
                                disabled={disabled}
                            />
                        )}
                    />
                ) : (
                    <Slider
                        id={id}
                        value={typeof value === 'number' ? value : parseFloat(value as string) || minValue}
                        onChange={(_, newValue) => {
                            if (onChange) {
                                const numValue = Array.isArray(newValue) ? newValue[0] : newValue;
                                onChange(numValue);
                            }
                        }}
                        min={minValue}
                        max={maxValue}
                        step={stepValue}
                        valueLabelDisplay="auto"
                        disabled={disabled}
                    />
                )}
                {fieldErrorMessage && (
                    <FormHelperText error>{fieldErrorMessage}</FormHelperText>
                )}
            </Box>
        )
    }

    if (type == 'select-multiple') {
        const fieldErrorMessage = getFieldErrorMessage(name);
        const selectedValues = Array.isArray(value) ? value.map(String) : [];
        const optionValues = options.map((option) => toSelectValue(option.value));
        const normalizedOptionValues = optionValues.map(String);
        const allOptionsSelected = normalizedOptionValues.length > 0
            && normalizedOptionValues.every((optionValue) => selectedValues.includes(optionValue));
        const selectAllValue = "__select_all__";
        const selectedChips: ChipItem[] = chipProps && chipProps.length > 0
            ? chipProps
            : options
                .filter((option) => selectedValues.includes(String(toSelectValue(option.value))))
                .map((option) => ({ label: option.name }));
        const handleMultipleChange = (event: SelectChangeEvent<Array<string | number>>) => {
            const selected = event.target.value;
            const nextValues = Array.isArray(selected)
                ? selected
                : String(selected).split(",");

            if (nextValues.map(String).includes(selectAllValue)) {
                onChange?.(allOptionsSelected ? [] : optionValues);
                return;
            }

            onChange?.(nextValues);
        };

        return (
            <Box sx={sx}>
                <Typography variant="subtitle">{label}</Typography>
                <Typography variant="label">{description}</Typography>
                {
                    !hideSelectedChips && selectedChips.length > 0 && selectedChips.map((chip, index) => {
                        return (
                            <Chip
                            key={index}
                            sx={{ marginTop: 2, marginRight: 1, marginBottom: 1 }}
                            label={chip.label}
                            onDelete={chip.onDelete}
                            onClick={chip.onClick || undefined}
                        />
                        )
                    })
                }
                <Select
                    multiple
                    displayEmpty
                    fullWidth
                    value={(value as string[] | number[] | undefined) || []}
                    onChange={handleMultipleChange}
                    renderValue={(selected) => {
                        const selectedArray = Array.isArray(selected) ? selected.map(String) : [];
                        if (selectedArray.length === 0) {
                            return (
                                <Typography variant="label">
                                    {placeholder || t('select')}
                                </Typography>
                            );
                        }
                        return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {options
                                    .filter((option) => selectedArray.includes(String(toSelectValue(option.value))))
                                    .map((option) => (
                                        <Chip
                                            key={String(option.value)}
                                            label={option.name}
                                            size="small"
                                            sx={{ maxWidth: '100%' }}
                                        />
                                    ))}
                            </Box>
                        );
                    }}
                    MenuProps={{
                        disableAutoFocusItem: true,
                        PaperProps: {
                            sx: {
                                maxWidth: 360,
                                width: 360,
                                maxHeight: 320,
                                overflowY: 'auto',
                            },
                        },
                        MenuListProps: {
                            dense: true,
                            sx: {
                                py: 0.5,
                            },
                        },
                    }}
                    error={!!fieldErrorMessage}
                    sx={{
                        '&.Mui-error': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#ef4444 !important',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#dc2626 !important',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#dc2626 !important',
                            },
                        },
                    }}
                    disabled={disabled}
                >
                    {selectAllLabel ? (
                        <MenuItem value={selectAllValue}>
                            <Checkbox
                                size="small"
                                checked={allOptionsSelected}
                                indeterminate={selectedValues.length > 0 && !allOptionsSelected}
                                sx={{ mr: 1 }}
                            />
                            {selectAllLabel}
                        </MenuItem>
                    ) : null}
                    {options.map((option, index) => (
                        <MenuItem key={index} value={toSelectValue(option.value)}>
                            <Checkbox
                                size="small"
                                checked={selectedValues.includes(String(toSelectValue(option.value)))}
                                sx={{ mr: 1 }}
                            />
                            {option.name}
                        </MenuItem>
                    ))}
                </Select>
                {fieldErrorMessage && (
                    <FormHelperText sx={{ color: '#ef4444', fontWeight: 500 }}>{fieldErrorMessage}</FormHelperText>
                )}
            </Box>
        )
    }

    if (type == 'file') {
        const VisuallyHiddenInput = styled('input')({
            clip: 'rect(0 0 0 0)',
            clipPath: 'inset(50%)',
            height: 1,
            overflow: 'hidden',
            position: 'absolute',
            bottom: 0,
            left: 0,
            whiteSpace: 'nowrap',
            width: 1,
        });
        return (
            <Box sx={sx}>
                <Typography variant="subtitle">{label}</Typography>
                <Typography variant="label">{description}</Typography>
                <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    startIcon={<CloudUploadIcon />}
                    disabled={disabled}
                >
                    {t('uploadFile')}
                    <VisuallyHiddenInput
                        type="file"
                        onChange={(event) => {
                            if (onChange) {
                                onChange(event.target.files?.[0]);
                            }
                        }}
                        multiple
                    />
                </Button>
            </Box>
        )
    }

    return null;
}

export default Input;
